/**
 * One delimited-text parser for every delimited surface: the client's F-16
 * clipboard (TSV, `tsv.ts`) and F-90 script import (CSV/TSV files,
 * `script-file.ts`), and — since the board agent (F-144) reads CSV/TSV
 * attachments on the SERVER — shared, so both sides parse a file the same way.
 * The client's `features/import-export/delimited.ts` re-exports it.
 *
 * They are the same grammar with a different delimiter — Excel's dialect,
 * which is RFC-4180 plus the leniencies real files carry: `""` escapes inside
 * quoted fields, quoted fields that span line breaks, and \n / \r\n / lone-\r
 * row separators mixed in one file.
 *
 * Pure: no DOM, no File, no Yjs — the whole parsing story is testable without
 * mounting anything.
 */
/** U+FEFF. A file saved by Excel (and most Windows editors) starts with one. */
const BOM = "﻿";
/**
 * Strip a leading byte-order mark. `TextDecoder("utf-8")` does NOT remove it
 * (only "utf-8" with `{ignoreBOM:false}`… which is not the default path a
 * `File.text()` takes), so without this the first cell of every Excel-exported
 * CSV silently begins with an invisible character — it compares unequal to the
 * same text typed by hand, and a glossary/translation-memory lookup misses.
 */
export function stripBom(text) {
    return text.startsWith(BOM) ? text.slice(BOM.length) : text;
}
/**
 * Parse delimited text into a matrix of records.
 *
 * Semantics that are decisions rather than obvious defaults:
 *  - A leading BOM is stripped (see `stripBom`).
 *  - `\r\n` and a lone `\r` are row separators; INSIDE a quoted field both
 *    normalize to `\n`, because a literal CR surviving into a cell value
 *    doubles the line break under `white-space: pre-wrap` and then round-trips
 *    into every copy and export.
 *  - A quote only opens a quoted field at the START of a field (`a"b"c` is the
 *    literal text `a"b"c`) — matching how Sheets/Excel read their own output.
 *  - A single trailing newline does not produce a phantom empty record (Excel
 *    appends one); empty input yields exactly one empty record, so callers
 *    always get a well-formed matrix rather than a special case.
 */
export function parseDelimited(input, delimiter) {
    const text = stripBom(input);
    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;
    let i = 0;
    const pushField = () => {
        row.push(field);
        field = "";
    };
    const pushRow = () => {
        pushField();
        rows.push(row);
        row = [];
    };
    while (i < text.length) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i += 2;
                    continue;
                }
                inQuotes = false;
                i++;
                continue;
            }
            if (ch === "\r") {
                field += "\n";
                i += text[i + 1] === "\n" ? 2 : 1;
                continue;
            }
            field += ch;
            i++;
            continue;
        }
        if (ch === '"' && field.length === 0) {
            inQuotes = true;
            i++;
            continue;
        }
        if (ch === delimiter) {
            pushField();
            i++;
            continue;
        }
        if (ch === "\n" || ch === "\r") {
            pushRow();
            i += ch === "\r" && text[i + 1] === "\n" ? 2 : 1;
            continue;
        }
        field += ch;
        i++;
    }
    // Final field/row — but a lone trailing newline must not add an empty row.
    if (field.length > 0 || row.length > 0 || rows.length === 0)
        pushRow();
    return rows;
}
//# sourceMappingURL=delimited.js.map